import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, X } from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/ui/empty-state';
import { WhyButton } from '@/components/why';
import { joinCase, leaveCase } from '@/lib/ws/socket';
import { SubCaseGroupsMenu } from './sub-case-groups-menu';
import { CaseHeader } from './case-header';
import { CaseStatStrip } from './case-stat-strip';
import { useCaseOverview } from './use-case-overview';
import { PassengersTab } from './passengers-tab/passengers-tab';
import { AllocationTab } from './allocation-tab/allocation-tab';
import { CostsTab } from './costs-tab/costs-tab';
import { AuditTab } from './audit-tab/audit-tab';
import { TimelineTab } from './timeline-tab/timeline-tab';

// Case-detail shell per docs/ui/operator-ui-tds.md §4.2 + §5.2.
//
// Five named-anchor tabs: passengers / allocation / costs / audit / timeline.
// Tab state lives in the URL hash (default #passengers), not query params, so
// deep links survive copy-paste. The ?highlight-passenger=<urn> query param
// is a separate concern — it lands on #passengers like everything else does.
//
// PASSENGERS IS THE CASE. A case is a set of PNRs being looked after, so the
// roster is what opens; the per-PNR drawer (PnrOperationCenter) is where hotel,
// meal, transport and re-accommodation actually get worked. Two tabs are gone:
//
//   • #overview — a second cockpit reading the same numbers the roster and its
//     drawer already show. Its KPIs are now one line in the header (CaseStatStrip)
//     and its case-level action (Resolve) went with them.
//   • #rebooking — literally the same PNR roster as #passengers, minus the search
//     and the acceptance filter. Re-accommodation lives in the PNR drawer.
//
// Both hashes still resolve (to #passengers) so old deep links do not 404.
//
// Tab content is forceMount'd (see components/ui/tabs.tsx) so switching is
// instant. Individual tab bodies fetch lazily via TanStack Query keyed by
// `['cases', caseUrn, <tab>]` — see TDS §6.1.

const TAB_VALUES = ['passengers', 'allocation', 'costs', 'audit', 'timeline'] as const;
type TabValue = (typeof TAB_VALUES)[number];

function isTabValue(value: string): value is TabValue {
  return (TAB_VALUES as readonly string[]).includes(value);
}

function activeTabFromHash(hash: string): TabValue {
  const stripped = hash.replace(/^#/, '');
  return isTabValue(stripped) ? stripped : 'passengers';
}

export function CaseDetailRoute() {
  const { t } = useTranslation('caseDetail');
  const { caseUrn = '' } = useParams<{ caseUrn: string }>();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const alreadyDeclared = searchParams.get('alreadyDeclared');
  const [showAlreadyDeclared, setShowAlreadyDeclared] = useState(!!alreadyDeclared);

  const overview = useCaseOverview(caseUrn);

  // Clear the alreadyDeclared param from the URL once shown (so refreshes don't
  // re-show it) while keeping it visible in state until dismissed.
  useEffect(() => {
    if (alreadyDeclared) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('alreadyDeclared');
      setSearchParams(newParams, { replace: true });
    }
  }, [alreadyDeclared, searchParams, setSearchParams]);

  // Subscribe to the per-case Socket.IO room once (TDS §5.4): the BFF fans out
  // StateChanged (incl. every sub-case lock change) to this room, so the
  // per-group lock indicators stay live. The socket is a no-op when not
  // connected, so this is safe in offline dev/tests.
  useEffect(() => {
    if (!caseUrn) return;
    joinCase(caseUrn);
    return () => leaveCase(caseUrn);
  }, [caseUrn]);

  const active = activeTabFromHash(hash);

  const onTabChange = (next: string): void => {
    navigate({ hash: `#${next}`, search: searchParams.toString() }, { replace: true });
  };

  if (!caseUrn) {
    return (
      <div className="p-6">
        <EmptyState
          title={t('shell.missingUrn.title')}
          description={t('shell.missingUrn.description')}
        />
      </div>
    );
  }

  return (
    // App-shell pane (frontend-conventions §18.1): fills its parent, never grows
    // the page. Only the tab content region below scrolls; header + tab bar stay put.
    <div className="flex h-full min-h-0 flex-col">
      <CaseHeader
        caseUrn={caseUrn}
        data={overview.data}
        isLoading={overview.isLoading}
        isError={overview.isError}
        aside={<CaseStatStrip caseUrn={caseUrn} />}
      />

      {showAlreadyDeclared ? (
        <div
          className="mx-4 mt-2 flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-100 sm:mx-6"
          role="status"
          data-testid="case-already-declared-banner"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t('shell.alreadyDeclared.title')}</p>
            <p className="mt-0.5 text-xs opacity-90">
              {alreadyDeclared === 'wholeFlight'
                ? t('shell.alreadyDeclared.wholeFlight')
                : t('shell.alreadyDeclared.withSelection', { count: parseInt(alreadyDeclared ?? '0', 10) || 0 })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAlreadyDeclared(false)}
            className="shrink-0 rounded p-0.5 hover:bg-amber-200/50 dark:hover:bg-amber-800/50"
            aria-label={t('shell.alreadyDeclared.dismiss')}
            data-testid="case-already-declared-dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}

      <Tabs
        value={active}
        onValueChange={onTabChange}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {/* Tab bar. The 67-chip sub-case rail is gone (TDS §5.2): one compact
            case-level lock indicator + a "N passenger groups" dropdown sit
            top-right, so PNR groups never dominate the screen. Tab list scrolls
            horizontally on narrow screens (§18.2). */}
        <div className="flex items-center justify-between gap-3 border-b border-border bg-background px-4 pt-3 sm:px-6">
          <div className="min-w-0 overflow-x-auto">
            <TabsList>
              <TabsTrigger value="passengers">{t('shell.tabs.passengers')}</TabsTrigger>
              <TabsTrigger value="allocation">{t('shell.tabs.allocation')}</TabsTrigger>
              <TabsTrigger value="costs">{t('shell.tabs.costs')}</TabsTrigger>
              <TabsTrigger value="audit">{t('shell.tabs.audit')}</TabsTrigger>
              <TabsTrigger value="timeline">{t('shell.tabs.timeline')}</TabsTrigger>
            </TabsList>
          </div>
          {/* Locks are per passenger-group now (on the allocation board's group
              cards), not case-level — so the groups dropdown sits here, plus the
              account ("¿Por qué?", feat/disruption-why W6).
              DELIBERATELY NOT A SIXTH TAB: two tabs were deleted from this file
              for being second cockpits over the same numbers (see the docblock),
              and the account is a READ you open, glance at and close — not a
              place to work. The header's governing-policy line
              (case-header.tsx) is its natural sibling: that line says WHICH
              policy governs, this says what the policy DID. */}
          <div className="flex shrink-0 items-center gap-2 pb-2">
            <WhyButton scope={{ kind: 'case', caseUrn }} variant="chip" testId="case-why-open" />
            <SubCaseGroupsMenu caseUrn={caseUrn} />
          </div>
        </div>

        {/* Bounded tab-content frame (§18.1): never scrolls the page. The active
            tab fills it (h-full) and owns its own scroll region(s) — simple tabs
            scroll their body; the allocation tab keeps its bar fixed and scrolls
            its two panes independently. mt-0 overrides TabsContent's default mt-4
            so h-full doesn't overflow. */}
        <div className="min-h-0 flex-1 overflow-hidden">
          <TabsContent value="passengers" className="mt-0 flex h-full flex-col overflow-hidden">
            <PassengersTab caseUrn={caseUrn} />
          </TabsContent>
          <TabsContent value="allocation" className="mt-0 flex h-full flex-col overflow-hidden">
            <AllocationTab caseUrn={caseUrn} />
          </TabsContent>
          <TabsContent value="costs" className="density-page mt-0 h-full overflow-y-auto p-4 sm:p-6">
            <CostsTab caseUrn={caseUrn} />
          </TabsContent>
          <TabsContent value="audit" className="density-page mt-0 h-full overflow-y-auto p-4 sm:p-6">
            <AuditTab caseUrn={caseUrn} />
          </TabsContent>
          <TabsContent value="timeline" className="density-page mt-0 h-full overflow-y-auto p-4 sm:p-6">
            <TimelineTab caseUrn={caseUrn} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// TabPlaceholder removed — all six tabs now have real implementations
// (NEX-159..164). See git history before NEX-162-163-164 for the dev stub pattern.
